import getUrlParameter from "helpers/getUrlParameter";
import Cookies from "js-cookie";
import { hasAnalyticsConsent } from "utils/cookieConsent";
import safely from "utils/safeCookie";
const utmTags = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const FBCLID_TAG = "fbclid";
// Mirrors PersistUtmTags/PersistTrackingCookies (apps/api/app/Http/Middleware) TTL of 2 years.
// Cookies.set expects days, not seconds.
const COOKIE_TTL_DAYS = 2 * 365;
// Escapes regex metacharacters in the key, matching helpers/getUrlParameter.js's convention.
const isParamPresent = (key) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`[?&]${escapedKey}=`).test(window.location.search);
};
const setCookie = (key, explicitValue) => {
    const value = explicitValue || getUrlParameter(key);
    if (value) {
        safely(() => Cookies.set(`cookie_${key}`, value, {
            expires: COOKIE_TTL_DAYS, path: "/", secure: true, sameSite: "Lax",
        }), undefined);
    }
    return value;
};
// Query params write their own cookies client-side because a cached CDN response never reaches
// Laravel, so the server-side middleware that would otherwise set these cookies never runs.
//
// utm_source/medium/campaign/term/content are necessary for attribution and aren't gated on
// analytics consent (persisted and read unconditionally, same as pds/pdm). They describe a
// single touch, so if any of them shows up in the URL, treat it as a fresh touch and clear
// whichever utm_* tags are missing from it — otherwise a partial link (e.g. just ?utm_source=)
// could leave a stale utm_medium/etc from an unrelated earlier touch mixed in.
export const persistUTM = () => {
    const isNewUtmTouch = utmTags.some((key) => isParamPresent(key));
    utmTags.forEach((key) => {
        const value = setCookie(key);
        if (!value && isNewUtmTouch) {
            safely(() => Cookies.remove(`cookie_${key}`, { path: "/" }), undefined);
        }
    });
};
// fbclid (Facebook's own click ID) is a distinct, independent signal from utm_* and — unlike
// them — is analytics-gated: only persisted once consent is granted, and removed if withdrawn.
// Accepts a value captured earlier (consent can resolve after client-side routing has already
// dropped fbclid from the URL); falls back to the current URL when none is supplied.
export const persistFbclid = (value) => {
    setCookie(FBCLID_TAG, value);
};
// Called when analytics consent is withdrawn, so a previously-set fbclid cookie doesn't linger.
export const removeFbclidCookie = () => {
    safely(() => Cookies.remove(`cookie_${FBCLID_TAG}`, { path: "/" }), undefined);
};
// Callers (e.g. donation/onboarding submission payloads) should expect this to omit `fbclid` —
// not just be empty — whenever analytics consent is denied and the value isn't live in the
// current URL. That's intentional (consent-denied visitors shouldn't have stored attribution
// resurface), so spread/destructure the result rather than assuming fbclid is always present.
// utm_* keys are always included when available, regardless of consent.
const retrieve = () => {
    const items = {};
    utmTags.forEach((key) => {
        const urlValue = getUrlParameter(key);
        const value = urlValue || safely(() => Cookies.get(`cookie_${key}`), undefined);
        if (value) {
            items[key] = value;
        }
    });
    const fbclidUrlValue = getUrlParameter(FBCLID_TAG);
    const fbclidValue = fbclidUrlValue
        || (hasAnalyticsConsent() ? safely(() => Cookies.get(`cookie_${FBCLID_TAG}`), undefined) : "");
    if (fbclidValue) {
        items[FBCLID_TAG] = fbclidValue;
    }
    return items;
};
export default retrieve;
